/**
 * Atlas Routes Panel Component
 * 
 * Left panel displaying routes from Atlas (FixedRoutesContext).
 * Allows users to view, search, and select routes for upload operations.
 * 
 * @module AtlasRoutesPanel
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import { useFixedRoutes } from '../../../../context/FixedRoutesContext';
import { RouteCard } from './RouteCard';
import { RouteSearchBar } from './RouteSearchBar';
import { useDebounce } from '../../../../hooks/useDebounce';
import type { FixedRoute } from '../../../../types/fixedRoutes';
import { useTranslation } from 'react-i18next';

/**
 * Props for AtlasRoutesPanel component
 */
export interface AtlasRoutesPanelProps {
  /** Selected route IDs */
  selectedRouteIds: Set<string>;
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Callback to update route count in parent header */
  onRouteCountChange?: (count: number, filteredCount?: number) => void;
  /** Callback to register refresh function */
  onRefreshRegister?: (refreshFn: () => void) => void;
}

/**
 * AtlasRoutesPanel Component
 * 
 * Displays routes from FixedRoutesContext with search and selection functionality.
 */
export const AtlasRoutesPanel: React.FC<AtlasRoutesPanelProps> = ({
  selectedRouteIds,
  onSelectionChange,
  onRouteCountChange,
  onRefreshRegister,
}) => {
  const { t } = useTranslation('mapTransfer');
  const { routes } = useFixedRoutes();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Sort routes alphabetically by name
  const sortedRoutes = useMemo(() => {
    return [...routes].sort((a, b) => a.name.localeCompare(b.name));
  }, [routes]);

  // Filter routes based on search query
  const filteredRoutes = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return sortedRoutes;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return sortedRoutes.filter((route) => {
      const nameMatch = route.name.toLowerCase().includes(query);
      const idMatch = route.id.toLowerCase().includes(query);
      return nameMatch || idMatch;
    });
  }, [sortedRoutes, debouncedSearchQuery]);

  // Update parent with route counts
  useEffect(() => {
    if (onRouteCountChange) {
      onRouteCountChange(
        sortedRoutes.length,
        debouncedSearchQuery.trim() ? filteredRoutes.length : undefined
      );
    }
  }, [sortedRoutes.length, filteredRoutes.length, debouncedSearchQuery, onRouteCountChange]);

  // Register refresh function with parent (routes come from context, so refresh is automatic)
  useEffect(() => {
    if (onRefreshRegister) {
      onRefreshRegister(() => {
        // Routes are automatically updated from context, so this is a no-op
        // The component will re-render when routes change in context
      });
    }
  }, [onRefreshRegister]);

  // Clear selection when routes change (routes added/removed from context)
  useEffect(() => {
    const currentRouteIds = new Set(sortedRoutes.map((r) => r.id));
    const validSelectedIds = Array.from(selectedRouteIds).filter((id) =>
      currentRouteIds.has(id)
    );
    if (validSelectedIds.length !== selectedRouteIds.size) {
      onSelectionChange(new Set(validSelectedIds));
    }
  }, [sortedRoutes, selectedRouteIds, onSelectionChange]);

  // Handle individual route selection
  const handleRouteSelect = (routeId: string, selected: boolean) => {
    const newSelection = new Set(selectedRouteIds);
    if (selected) {
      newSelection.add(routeId);
    } else {
      newSelection.delete(routeId);
    }
    onSelectionChange(newSelection);
  };

  // Handle select all
  const handleSelectAll = () => {
    const allFilteredIds = new Set(filteredRoutes.map((r) => r.id));
    onSelectionChange(allFilteredIds);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    onSelectionChange(new Set());
  };

  const selectedCount = selectedRouteIds.size;
  const allFilteredSelected =
    filteredRoutes.length > 0 &&
    filteredRoutes.every((route) => selectedRouteIds.has(route.id));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Search Bar */}
      <Box sx={{ padding: 2, paddingBottom: 1 }}>
        <RouteSearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          placeholder={t('routes.panel.searchPlaceholderAtlas')}
        />

        {/* Selection Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allFilteredSelected}
                  indeterminate={
                    selectedCount > 0 && !allFilteredSelected
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSelectAll();
                    } else {
                      handleDeselectAll();
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t('routes.panel.selectAllLabel', { count: filteredRoutes.length })}
                </Typography>
              }
            />
          </Box>

          {selectedCount > 0 && (
            <Typography variant="body2" color="primary">
              {t('routes.panel.selectedLabel', { count: selectedCount })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Route List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Critical for flex scrolling
          overflow: 'auto',
          paddingX: 2,
          paddingBottom: 2,
        }}
      >
        {filteredRoutes.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: 4,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery.trim()
                ? t('routes.panel.noMatch')
                : t('routes.panel.emptyAtlas')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim()
                ? t('routes.panel.noMatchHint')
                : t('routes.panel.emptyAtlasHint')}
            </Typography>
          </Box>
        ) : (
          filteredRoutes.map((route) => (
            <RouteCard
              key={route.id}
              route={route}
              source="atlas"
              selected={selectedRouteIds.has(route.id)}
              onSelect={(selected) => handleRouteSelect(route.id, selected)}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

